import ReactDOM from 'react-dom';
import React from 'react';
import { useTranslation } from 'react-i18next';
import DocumentFolderPdfModal from '../DocumentFolderPdfModal';
import { ConfirmModal } from '../ui/Modal';
import CloudFullModal from '../ui/CloudFullModal';
import ContextMenu from '../ui/ContextMenu';
import { rowMenuItems } from './rowMenu';
import { locationActionFor } from '../../plans/cloudAccess';
import { DuplicateDialog, MoveToDialog } from './RowDialogs';
import useDocProjectRows from './useDocProjectRows';

/**
 * Everything a section floats over the app: the row menu and the dialogs it
 * opens, the cloud-full notice, the folder-PDF options and the delete confirm.
 *
 * All PORTALLED to <body>: `Modal` is `position: fixed` but does not portal,
 * and inside the sidebar's scroll container (its own stacking context) a
 * dialog painted under the app chrome — its buttons could not be clicked.
 */
export default function SectionDialogs({ scope, tree, actions, exporting, commands, pins, rowMenu, closeRowMenu, workspaces = [] }) {
  const { t } = useTranslation();
  const { deleteRequest, setDeleteRequest } = actions;
  const projectRowsFor = useDocProjectRows(scope);
  const location = {
    canMove: actions.canMoveDocumentLocation,
    onDevice: actions.onDeviceSection,
    moving: actions.movingDocId != null,
    move: actions.moveDocumentLocation,
    // Plans v2 card 3.4: Move to cloud / Keep on this device / Download to this device.
    action: locationActionFor(actions.onDeviceSection ? 'device' : 'cloud'),
  };

  return (
    <>
      {rowMenu && (
        <ContextMenu
          x={rowMenu.x}
          y={rowMenu.y}
          keyboard
          label={rowMenu.node.name || t('docSidebar.untitled')}
          className="doc-row-menu"
          items={rowMenuItems({
            t,
            node: rowMenu.node,
            pinned: pins.pinnedSet.has(String(rowMenu.node.id)),
            busy: exporting.busy,
            location,
            cmds: commands.cmds,
            projectRows: projectRowsFor(rowMenu.node),
          }).map(function wrap(item) {
            // A picked row must not hand focus back to the tree row afterwards
            // (it may have opened a rename field or a dialog) — only a menu
            // dismissed with Escape / an outside click does.
            if (!item || item.separator) return item;
            if (item.items) return { ...item, items: item.items.map(wrap) };
            return { ...item, onClick: () => { closeRowMenu(true); item.onClick?.(); } };
          })}
          onClose={() => closeRowMenu(false)}
        />
      )}

      {actions.cloudFull &&
        ReactDOM.createPortal(<CloudFullModal isOpen kind="documents" onClose={() => actions.setCloudFull(false)} />, document.body)}

      {exporting.pdfOptions &&
        ReactDOM.createPortal(
          <DocumentFolderPdfModal
            isOpen
            folderName={exporting.pdfOptions.node.name || t('docSidebar.untitledFolder')}
            documentCount={exporting.pdfOptions.documentCount}
            onCancel={() => exporting.setPdfOptions(null)}
            onConfirm={(options) => exporting.runFolderPdfExport(exporting.pdfOptions.node, options)}
          />,
          document.body
        )}

      {deleteRequest &&
        ReactDOM.createPortal(
          <ConfirmModal
            isOpen
            onClose={() => setDeleteRequest(null)}
            onConfirm={() => actions.performDelete(deleteRequest.subtree)}
            title={deleteRequest.title}
            message={deleteRequest.message}
            confirmText={t('docSidebar.delete.confirm')}
            cancelText={t('common.cancel')}
            type="danger"
          />,
          document.body
        )}

      {commands.duplicateAsk && (
        <DuplicateDialog
          node={commands.duplicateAsk}
          onCancel={() => commands.setDuplicateAsk(null)}
          onDuplicate={commands.runDuplicate}
        />
      )}

      {commands.moveNodeAsk && (
        <MoveToDialog
          node={commands.moveNodeAsk}
          nodes={tree.nodes}
          scope={scope}
          workspaces={workspaces}
          onClose={() => commands.setMoveNodeAsk(null)}
          onMoved={async () => {
            commands.setMoveNodeAsk(null);
            await tree.load();
          }}
        />
      )}
    </>
  );
}
